import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { Product } from '../types/product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { fetchProducts } from '../utils/products';
import Breadcrumb from '../components/Breadcrumb';

const ProductsPage: React.FC = () => {
  const location = useLocation();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('default');
  const [productType, setProductType] = useState<'all' | 'subscription'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [revealedProducts, setRevealedProducts] = useState<Product[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [productCategory, setProductCategory] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get('type');
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');

    if (typeParam === 'subscription') {
        setProductType('subscription');
    } else {
        setProductType('all');
    }

    if (categoryParam) {
      setProductCategory(categoryParam);
    } else {
      setProductCategory(null);
    }

    if (searchParam) {
      setSearchTerm(searchParam);
    }

  const loadProducts = async () => {
    try {
      setIsLoading(true);
        const fetchedProducts = await fetchProducts();
        setProducts(fetchedProducts);
        setFilteredProducts(fetchedProducts);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to load products');
      setIsLoading(false);
    }
  };

    loadProducts();
  }, [location.search]);

  const filterProducts = (productList: Product[]) => {
    let result = productList;

    if (productType === 'subscription') {
      result = result.filter(product => product.isSubscription);
    }

    if (productCategory) {
      result = result.filter(product => 
        product.category?.toLowerCase() === productCategory.toLowerCase() || 
        product.metadata?.category?.toLowerCase() === productCategory.toLowerCase()
      );
    }

    if (searchTerm) {
      result = result.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.shortDescription && product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    switch (sortOption) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    setFilteredProducts(result);
    setRevealedProducts([]);
  };

  useEffect(() => {
    if (!isLoading && filteredProducts.length > 0) {
      const revealInterval = setInterval(() => {
        setRevealedProducts(prev => {
          if (prev.length < filteredProducts.length) {
            return [...prev, filteredProducts[prev.length]];
          }
          clearInterval(revealInterval);
          return prev;
        });
      }, 200);

      return () => clearInterval(revealInterval);
    }
  }, [isLoading, filteredProducts]);

  useEffect(() => {
    if (products.length > 0) {
      filterProducts(products);
    }
  }, [searchTerm, sortOption, productType, products]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (option: string) => {
    setSortOption(option);
    setIsDropdownOpen(false);
  };

  if (isLoading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mb-6 relative w-48 h-48 mx-auto">
            <div className="absolute inset-0 animate-ping bg-[#2b603e]/20 rounded-full"></div>
            <div className="absolute inset-0 border-8 border-t-8 border-[#2b603e] border-t-transparent rounded-full animate-spin-slow"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <img 
                src={require('../assets/images/logowithoutlogo.png')} 
                alt="Asclepius Logo" 
                className="w-24 h-24 rounded-full object-cover z-10" 
              />
            </div>
          </div>
          <p className="text-2xl text-gray-600 font-semibold">
            Harvesting Nature's Finest
          </p>
          <p className="text-gray-500 mt-2">Preparing your Himalayan supplements</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb 
          items={[
            { label: 'Home', link: '/' },
            { label: 'Products', link: '/products' }
          ]} 
        />

        {/* Page Header */}
        <div className="bg-white text-gray-800 py-12 shadow-sm">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[#2b603e] to-[#1a3d25]">
              {productCategory === 'shilajit' 
                ? 'Asclepius Shilajit' 
                : productCategory === 'essentials' 
                  ? 'Asclepius Essentials'
                  : productType === 'subscription' 
                    ? 'Subscription Products' 
                    : 'Our Products'}
            </h1>
            {productCategory && (
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {productCategory === 'shilajit' 
                  ? 'Discover the pure power of our premium Himalayan Shilajit' 
                  : productCategory === 'essentials' 
                    ? 'Explore our carefully curated wellness essentials' 
                    : ''}
              </p>
            )}
            {productType === 'subscription' && (
              <p className="text-lg text-green-700 font-semibold mt-2 text-center">
                Get 17% off on all subscription products!
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="container mx-auto px-4">
          <hr className="border-t border-gray-200 my-0" />
        </div>

        {/* Products Filter and Sort */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 gap-4">
            {/* Search Input */}
            <div className="w-full md:w-1/2 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-200 transition-all duration-300"
              />
            </div>
            
            {/* Custom Dropdown */}
            <div className="w-full md:w-1/4 relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-200 transition-all duration-300"
              >
                <span className="flex items-center">
                  <FontAwesomeIcon icon={faSort} className="mr-2 text-gray-500" />
                  {sortOption === 'default' ? 'Sort by' : 
                   sortOption === 'price-asc' ? 'Price: Low to High' :
                   sortOption === 'price-desc' ? 'Price: High to Low' :
                   'Top Rated'}
                </span>
                <FontAwesomeIcon icon={faChevronDown} className="text-gray-500" />
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                  <ul className="py-1">
                    <li 
                      onClick={() => handleSortChange('default')} 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      Default
                    </li>
                    <li 
                      onClick={() => handleSortChange('price-asc')} 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      Price: Low to High
                    </li>
                    <li 
                      onClick={() => handleSortChange('price-desc')} 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      Price: High to Low
                    </li>
                    <li 
                      onClick={() => handleSortChange('rating')} 
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      Top Rated
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="container mx-auto px-4 py-8">
          {revealedProducts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg shadow-md">
              <div className="mb-6">
                <svg className="mx-auto h-24 w-24 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.723 1.745 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <p className="text-xl text-gray-600">No products found matching your search.</p>
              <p className="text-gray-500 mt-2">Try adjusting your search or filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {revealedProducts.map((product, index) => (
                <div 
                  key={product.id} 
                  className="opacity-0 animate-fade-in"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination or Load More (placeholder) */}
        {revealedProducts.length > 0 && (
          <div className="container mx-auto px-4 py-8 text-center">
            <p className="text-gray-600 mb-4">
              Showing {revealedProducts.length} of {products.length} products
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;